import { FilePermissionChangedEvent } from '@rematter/frontend-lib-api-client/grpc/files/v1/file_events_pb';
import { FilePermissionChange } from '@rematter/frontend-lib-api-client/grpc/files/v1/file_pb';
import { Col, humanReadableDateTime } from '@rematter/ui-web';
import { FieldDisplay, IDField } from '@rematter/ui-web';

import { filePermissionActionLabels } from '../enumLabels';

import { protoTimestampToZonedDateTimeString } from '@/audit-log/_components/_shared';
import { getLocale } from '~/shared/utils/locale';

type FilePermissionChangedDetailProps = {
  event: FilePermissionChangedEvent;
};

const formatFileSize = (size: bigint): string => {
  const bytes = Number(size);
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const FilePermissionChangedDetail = ({
  event,
}: FilePermissionChangedDetailProps) => {
  const file = event.file;
  const changedAtStr = protoTimestampToZonedDateTimeString(event.changedAt);

  if (!file) {
    return (
      <Col className="@FilePermissionChangedDetail items-center justify-center p-4 text-neutral/40">
        <span>No data available</span>
      </Col>
    );
  }

  return (
    <Col className="@FilePermissionChangedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          File Details
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <IDField label="ID" value={file.id?.value} />
          <FieldDisplay label="File Name" value={file.fileName} />
          <FieldDisplay
            label="File Size"
            value={formatFileSize(file.fileSize)}
          />
          <FieldDisplay label="MIME Type" value={file.mimeType} />
        </div>
      </Col>

      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          Permission Change Details
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <FieldDisplay label="Changed By" value={event.changedBy} />
          {changedAtStr && (
            <FieldDisplay
              label="Changed At"
              value={humanReadableDateTime(changedAtStr, {
                locale: getLocale(),
              })}
            />
          )}
        </div>
      </Col>

      {event.changes.length > 0 && (
        <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
          <span className="text-xs font-medium text-neutral/60">
            Permission Changes
          </span>
          <div className="space-y-2">
            {event.changes.map(
              (change: FilePermissionChange, index: number) => (
                <div
                  key={index}
                  className="grid grid-cols-3 gap-x-4 rounded bg-neutral/5 p-2 text-sm"
                >
                  <FieldDisplay label="User ID" value={change.userId} />
                  <FieldDisplay label="Permission" value={change.permission} />
                  <FieldDisplay
                    label="Action"
                    value={filePermissionActionLabels[change.action]}
                  />
                </div>
              ),
            )}
          </div>
        </Col>
      )}
    </Col>
  );
};
